import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const KinJoin = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const { user, loading, login, setUser } = useAuth();
  const navigate = useNavigate();
  
  const [invitation, setInvitation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [useInvitedEmail, setUseInvitedEmail] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);

  useEffect(() => {
    if (!code) {
      setError('Invalid invitation link');
      setIsLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        const response = await fetch(`/api/kin/invite/${code}`);
        if (!response.ok) {
          throw new Error('Failed to load invitation');
        }
        const data = await response.json();
        setInvitation(data);
        setLoginEmail(data.invitedEmail || '');
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [code]);

useEffect(() => {
  // If user is logged in and we have an invitation, check if they match
  if (!loading && user && invitation) {
    // If the logged-in user matches the invited email, they should be able to accept
    if (user.email === invitation.invitedEmail) {
      // If they're already a Kin member, redirect to dashboard
      if (user.activeKinId) {
        navigate('/kin-dashboard');
        return;
      }
      // If they've already accepted, redirect to dashboard
      if (invitation.status === 'accepted') {
        navigate('/kin-dashboard');
        return;
      }
      // Otherwise, they can accept the invitation
      // The UI will show the accept button
    }
  }
}, [user, loading, invitation, navigate]);

  const handleAccept = async () => {
    if (!user) {
      setShowLogin(true);
      return;
    }

    setIsAccepting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/kin/invite/${code}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to accept invitation');
      }

      const data = await response.json();
      
      // Force refresh user data to get updated activeKinId
      const meResponse = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (meResponse.ok) {
        const userData = await meResponse.json();
        setUser(userData);  // This updates the auth context
      }
      
      navigate('/kin-dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);
    try {
      await login(loginEmail, loginPassword);
      setShowLogin(false);
    } catch (err) {
      setLoginError(err.response?.data?.message || 'Login failed. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">Loading your invitation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <div className="text-4xl mb-4">😕</div>
          <h1 className="text-xl font-bold mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="bg-green-600 text-white px-6 py-2 rounded-lg">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <div className="text-4xl mb-4">🔍</div>
          <h1 className="text-xl font-bold mb-2">Invitation Not Found</h1>
          <p className="text-gray-600 mb-4">This invitation may have expired.</p>
          <button onClick={() => navigate('/')} className="bg-green-600 text-white px-6 py-2 rounded-lg">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Existing user detected - show recognition
  if (invitation.existingUser && !user) {
    const existing = invitation.existingUser;
    const statusLabels = {
      'registered': `✅ Registered ${existing.role ? `(${existing.role})` : ''}`,
      'curious_plus': '📧 Curious+ (has completed modules)',
      'curious': '🔍 Curious (started exploring)',
      'new': '🆕 New user'
    };

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">👋</div>
            <h1 className="text-2xl font-bold">Welcome back!</h1>
            <p className="text-gray-600 mt-2">
              {invitation.invitedBy?.name} invited you to join <strong>{invitation.kin?.name}</strong>
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              We recognize <strong>{existing.email}</strong> from your previous activity.
            </p>
            <p className="text-sm text-blue-700 mt-1">
              Status: <strong>{statusLabels[existing.status] || 'Active'}</strong>
            </p>
            {existing.coursesCompleted > 0 && (
              <p className="text-sm text-blue-700">
                📚 Completed {existing.coursesCompleted} modules
              </p>
            )}
            {existing.hasCredits && (
              <p className="text-sm text-blue-700">
                💰 Has earned credits
              </p>
            )}
          </div>

          <div className="space-y-3">
            <button
              onClick={() => setShowLogin(true)}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              Log in with {existing.email}
            </button>
            <Link
              to={`/register?invite=${code}`}
              className="block w-full bg-gray-200 text-gray-700 py-2 rounded-lg text-center hover:bg-gray-300"
            >
              Register with a different email
            </Link>
          </div>

          {showLogin && (
            <div className="mt-4 pt-4 border-t">
              <form onSubmit={handleLogin} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                {loginError && (
                  <p className="text-red-600 text-sm">{loginError}</p>
                )}
                <button
                  type="submit"
                  className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                >
                  Log In
                </button>
                <button
                  type="button"
                  onClick={() => setShowLogin(false)}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    );
  }

  // User is logged in with the right email - show accept
  if (user && user.email === invitation.invitedEmail) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">👋</div>
            <h1 className="text-2xl font-bold">You're Invited!</h1>
            <p className="text-gray-600 mt-2">
              {invitation.invitedBy?.name} invited you to join <strong>{invitation.kin?.name}</strong>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Logged in as: <strong>{user.email}</strong>
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-600">Your current status:</p>
            <p className="font-medium">
              {invitation.existingUser ? (
                invitation.existingUser.role ? `✅ Registered (${invitation.existingUser.role})` : '📧 Curious+'
              ) : '🆕 Ready to join'}
            </p>
            {invitation.existingUser?.coursesCompleted > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                📚 Completed {invitation.existingUser.coursesCompleted} modules
              </p>
            )}
          </div>

          {invitation.status === 'accepted' ? (
            <button 
              onClick={() => navigate('/kin-dashboard')}
              className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
            >
              Go to Kin Dashboard
            </button>
          ) : (
            <button 
              onClick={handleAccept}
              disabled={isAccepting}
              className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isAccepting ? 'Accepting...' : '✅ Accept Invitation'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // New user - no existing account
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">👋</div>
          <h1 className="text-2xl font-bold">You're Invited!</h1>
          <p className="text-gray-600 mt-2">
            {invitation.invitedBy?.name} invited you to join <strong>{invitation.kin?.name}</strong>
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Invitation sent to: <strong>{invitation.invitedEmail}</strong>
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            This invitation was sent to <strong>{invitation.invitedEmail}</strong>.
            You can only register using this email. If you need to register with a different email address, 
    ask the Coordinator to send a new invitation to that email.
          </p>
        </div>
        
        <div className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            id="useInvitedEmail"
            checked={useInvitedEmail}
            onChange={(e) => setUseInvitedEmail(e.target.checked)}
            className="w-4 h-4"
          />
          <label htmlFor="useInvitedEmail" className="text-sm text-gray-700">
            Use {invitation.invitedEmail} to register
          </label>
        </div>

        <div className="space-y-3">
          {useInvitedEmail ? (
            <Link 
              to={`/kin-register?email=${invitation.invitedEmail}&code=${code}`}
              className="block w-full bg-green-600 text-white py-2 rounded-lg text-center hover:bg-green-700"
            >
              Register with this email
            </Link>
          ) : (
            <Link 
              to={`/kin-register?code=${code}`}
              className="block w-full bg-blue-600 text-white py-2 rounded-lg text-center hover:bg-blue-700"
            >
              Register with a different email
            </Link>
          )}

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Already have an account?</span>
            </div>
          </div>

          <Link 
            to={`/login?return=/kin-join?code=${code}`}
            className="block w-full bg-gray-200 text-gray-700 py-2 rounded-lg text-center hover:bg-gray-300"
          >
            Log In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KinJoin;
